import { Activity, AlertTriangle, CheckCircle2, RefreshCw, Server } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { api, getApiErrorMessage } from '../../api/client';
import type { SystemStatus } from '../../api/types';
import { formatDateTime } from '../../utils/cargoStatus';

function boolBadge(ok: boolean, yes = 'Yes', no = 'No') {
  return (
    <span className={ok ? 'badge badge-success' : 'badge badge-blocked'}>
      {ok ? yes : no}
    </span>
  );
}

function StatCard({
  label,
  value,
  unit,
  hint,
  icon: Icon,
  warn,
  tone = 'neutral',
}: {
  label: string;
  value: string | number;
  unit?: string;
  hint?: string;
  icon?: LucideIcon;
  warn?: boolean;
  tone?: 'green' | 'amber' | 'red' | 'neutral';
}) {
  const toneColor =
    tone === 'green'
      ? 'var(--onyx-green)'
      : tone === 'amber'
        ? 'var(--onyx-amber)'
        : tone === 'red'
          ? 'var(--onyx-red)'
          : 'var(--onyx-text)';
  const toneBg =
    tone === 'green'
      ? 'var(--onyx-green-dim)'
      : tone === 'amber'
        ? 'rgba(245, 158, 11, 0.12)'
        : tone === 'red'
          ? 'var(--onyx-red-dim)'
          : 'rgba(255,255,255,0.06)';

  return (
    <div className={`admin-stat cargo-stat-card cargo-metric-card${warn ? ' cargo-metric-warn' : ''}`}>
      {Icon ? (
        <div className="admin-stat-icon" style={{ background: toneBg }}>
          <Icon size={18} color={toneColor} aria-hidden />
        </div>
      ) : null}
      <div className="stat-label">{label}</div>
      <div className="cargo-metric-value-row">
        <span className="cargo-stat-value" style={{ color: warn ? 'var(--onyx-red)' : toneColor }}>
          {value}
        </span>
        {unit ? <span className="cargo-metric-unit">{unit}</span> : null}
      </div>
      {hint ? <div className="cargo-stat-hint">{hint}</div> : null}
    </div>
  );
}

function ConfigRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="cargo-detail-row">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

export function SystemSection() {
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.systemStatus();
      setStatus(res.status);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const id = setInterval(() => void load(), 60_000);
    return () => clearInterval(id);
  }, [load]);

  const degraded = status?.health.overall !== 'healthy';
  const tron = status?.tron;
  const tronIssues =
    tron &&
    (!tron.gas_sponsor_configured ||
      !tron.auto_fund_deposit_trx ||
      !tron.commission_wallet_configured ||
      (tron.energy_rental_enabled && !tron.energy_api_key_configured));

  return (
    <>
      <div className="section-header system-section-header">
        <div>
          <h1 className="console-title">System &amp; Ops</h1>
          <p className="console-sub">
            Cargo payments · Tron split pipeline · webhooks · live config (no secrets)
          </p>
        </div>
        <button type="button" className="btn-outline btn-sm" disabled={loading} onClick={() => void load()}>
          <RefreshCw size={14} aria-hidden className={loading ? 'spin-icon' : undefined} />
          Refresh
        </button>
      </div>

      {error ? <p className="form-error">{error}</p> : null}

      {status ? (
        <>
          <div className={`system-health-banner${degraded ? ' system-health-banner-warn' : ''}`}>
            {degraded ? <AlertTriangle size={18} /> : <CheckCircle2 size={18} />}
            <div>
              <strong>{degraded ? 'Degraded' : 'Healthy'}</strong>
              <span>
                Postgres {status.health.postgres} · updated {formatDateTime(status.timestamp)}
              </span>
            </div>
          </div>

          {tronIssues ? (
            <div className="cargo-split-retry-banner">
              <strong>Tron split risk:</strong> check gas sponsor, auto-fund, commission wallet, and energy API
              key in server env.
            </div>
          ) : null}

          {status.cargo.split_stale_processing > 0 ? (
            <div className="cargo-split-retry-banner">
              <strong>{status.cargo.split_stale_processing} split(s) stuck in processing</strong> — use Cargo
              invoices → Retry split or SQL reset to pending.
            </div>
          ) : null}

          <div className="cargo-panel">
            <h2 className="cargo-panel-title">
              <Activity size={16} aria-hidden /> Cargo invoices
            </h2>
            <div className="cargo-stats-grid">
              <StatCard
                label="Partners active"
                value={status.cargo.companies_active}
                unit="companies"
                hint="Ready to accept payments"
                icon={CheckCircle2}
                tone="green"
              />
              <StatCard
                label="No payout wallet"
                value={status.cargo.companies_no_wallet}
                unit="partners"
                hint="Split will fail until wallet is set"
                icon={AlertTriangle}
                warn={status.cargo.companies_no_wallet > 0}
                tone="amber"
              />
              <StatCard
                label="Invoices pending"
                value={status.cargo.invoices_pending}
                unit="open"
                hint="Awaiting customer USDT"
                icon={Activity}
                tone="amber"
              />
              <StatCard
                label="Invoices paid"
                value={status.cargo.invoices_paid}
                unit="settled"
                hint="Deposit confirmed on-chain"
                icon={CheckCircle2}
                tone="green"
              />
              <StatCard
                label="Invoices expired"
                value={status.cargo.invoices_expired}
                unit="closed"
                hint="TTL elapsed without payment"
                tone="neutral"
              />
              <StatCard
                label="Volume paid"
                value={Number.parseFloat(status.cargo.volume_paid_usdt).toLocaleString('en-US', {
                  maximumFractionDigits: 2,
                })}
                unit="USDT"
                hint="Total paid invoice volume"
                tone="neutral"
              />
            </div>
          </div>

          <div className="cargo-panel">
            <h2 className="cargo-panel-title">
              <Server size={16} aria-hidden /> Split pipeline
            </h2>
            <div className="cargo-stats-grid">
              <StatCard
                label="Split pending"
                value={status.cargo.split_pending}
                unit="queued"
                hint="Waiting for worker"
                tone="amber"
              />
              <StatCard
                label="Split processing"
                value={status.cargo.split_processing}
                unit="in flight"
                hint="Commission + vendor payout"
                tone="neutral"
              />
              <StatCard
                label="Split failed"
                value={status.cargo.split_failed}
                unit="need retry"
                hint="Use Retry split in Cargo admin"
                icon={AlertTriangle}
                warn={status.cargo.split_failed > 0}
                tone="red"
              />
              <StatCard
                label="Split completed"
                value={status.cargo.split_completed}
                unit="done"
                hint="Funds sent to vendor + commission"
                icon={CheckCircle2}
                tone="green"
              />
              <StatCard
                label="Stale processing"
                value={status.cargo.split_stale_processing}
                unit="stuck"
                hint="Processing longer than expected"
                icon={AlertTriangle}
                warn={status.cargo.split_stale_processing > 0}
                tone="red"
              />
              <StatCard
                label="Webhook fails"
                value={status.webhooks.failed_24h}
                unit="last 24h"
                hint={`${status.webhooks.deliveries_24h} deliveries total`}
                icon={AlertTriangle}
                warn={status.webhooks.failed_24h > 0}
                tone="red"
              />
            </div>
          </div>

          <div className="system-panels-row">
            <div className="cargo-panel system-config-panel">
              <h2 className="cargo-panel-title">Tron &amp; invoices config</h2>
              <div className="cargo-detail-list">
                <ConfigRow label="Network" value={tron?.network ?? '—'} />
                <ConfigRow label="Monitor" value={boolBadge(Boolean(tron?.monitor_enabled))} />
                <ConfigRow label="Monitor interval" value={`${tron?.monitor_interval_ms ?? 0} ms`} />
                <ConfigRow label="Split retry interval" value={`${tron?.split_retry_interval_ms ?? 0} ms`} />
                <ConfigRow label="Auto-fund deposit TRX" value={boolBadge(Boolean(tron?.auto_fund_deposit_trx))} />
                <ConfigRow label="Gas sponsor key" value={boolBadge(Boolean(tron?.gas_sponsor_configured), 'set', 'MISSING')} />
                <ConfigRow label="Energy rental" value={boolBadge(Boolean(tron?.energy_rental_enabled))} />
                <ConfigRow label="Energy provider" value={tron?.energy_provider ?? '—'} />
                <ConfigRow label="Energy API key" value={boolBadge(Boolean(tron?.energy_api_key_configured), 'set', 'MISSING')} />
                <ConfigRow label="Energy balance TRX" value={tron?.energy_provider_balance_trx ?? tron?.energy_provider_balance_error ?? '—'} />
                <ConfigRow label="Sweep deposit TRX" value={boolBadge(Boolean(tron?.sweep_deposit_trx))} />
                <ConfigRow label="Commission wallet" value={boolBadge(Boolean(tron?.commission_wallet_configured), 'set', 'MISSING')} />
                <ConfigRow label="USDT contract" value={<code className="cargo-hash">{tron?.usdt_contract}</code>} />
                <ConfigRow label="Default invoice TTL" value={`${tron?.invoice_ttl_minutes ?? 0} min`} />
                <ConfigRow label="Max invoice TTL (API)" value={`${tron?.invoice_max_ttl_minutes ?? 0} min`} />
              </div>
            </div>

            <div className="cargo-panel system-config-panel">
              <h2 className="cargo-panel-title">Webhooks &amp; URLs</h2>
              <div className="cargo-detail-list">
                <ConfigRow label="Deliveries (24h)" value={status.webhooks.deliveries_24h} />
                <ConfigRow label="Failed (24h)" value={status.webhooks.failed_24h} />
                <ConfigRow
                  label="Last failure"
                  value={status.webhooks.last_failure_at ? formatDateTime(status.webhooks.last_failure_at) : '—'}
                />
                <ConfigRow label="API" value={status.urls.api_base} />
                <ConfigRow label="Pay page" value={status.urls.pay_base} />
                <ConfigRow label="Admin" value={status.urls.admin_base} />
              </div>
              <p className="hint system-hint">
                Partner tracking integration: <code>POST /v1/invoices</code> with{' '}
                <code>cargo_id</code>, <code>ttl_days</code>, <code>reuse_if_open: true</code>. See{' '}
                <code>docs/CARGO_TRACKING_API.md</code>.
              </p>
            </div>
          </div>
        </>
      ) : loading ? (
        <p className="cargo-empty-row">Loading system status…</p>
      ) : null}
    </>
  );
}
